import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { StateBlock } from "../components/Cards";
import { displayUserName, UserAvatar } from "../components/UserAvatar";
import { useAuth } from "../lib/auth";
import {
  getAllUsers,
  getPendingReviewPosts,
  getPostReviewMode,
  getSections,
  getUser,
  POST_REVIEW_MODE_CLOSED,
  POST_REVIEW_MODE_OPEN,
  createSection,
  deleteSection,
  reviewPost,
  setPostReviewMode,
  updateOrSetSection,
  updateManagedSections,
  updateUserRole
} from "../lib/data";
import { setSeo } from "../lib/seo";
import { useAsync } from "../lib/useAsync";
import type { ForumUser, ReviewPost, Section, UserRole } from "../lib/types";

const roles: UserRole[] = ["ADMIN", "COADMIN", "USER"];

export function AdminDashboard() {
  const { user } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const { data: currentUser, loading: currentUserLoading, error: currentUserError } = useAsync(
    () => (user ? getUser(user.uid) : Promise.resolve(null)),
    [user?.uid, refreshKey]
  );
  const role = normalizeRole(currentUser?.role);
  const canUseDashboard = role === "ADMIN" || role === "COADMIN";
  const isAdmin = role === "ADMIN";

  const { data: users } = useAsync(() => (isAdmin ? getAllUsers() : Promise.resolve([])), [isAdmin, refreshKey]);
  const { data: sections } = useAsync(() => (isAdmin ? getSections() : Promise.resolve([])), [isAdmin, refreshKey]);
  const { data: reviewMode } = useAsync(() => (isAdmin ? getPostReviewMode() : Promise.resolve(POST_REVIEW_MODE_CLOSED)), [isAdmin, refreshKey]);
  const { data: pendingPosts, loading: pendingLoading, error: pendingError } = useAsync(getPendingReviewPosts, [refreshKey]);

  const visiblePendingPosts = useMemo(() => {
    if (!currentUser || !pendingPosts) return [];
    return pendingPosts.filter((item) => canReviewPost(currentUser, item));
  }, [currentUser, pendingPosts]);

  useEffect(() => setSeo({ title: "Admin Dashboard | My Local Forum", path: "/admin", noindex: true }), []);

  async function runAction(action: () => Promise<void>, success: string) {
    setNotice(null);
    try {
      await action();
      setNotice(success);
      setRefreshKey((key) => key + 1);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Action failed.");
    }
  }

  if (!user) {
    return (
      <section className="page">
        <h1>Admin Dashboard</h1>
        <Link className="primary-button" to="/auth">Sign in</Link>
      </section>
    );
  }

  if (currentUserLoading || currentUserError || !canUseDashboard) {
    return (
      <section className="page">
        <h1>Admin Dashboard</h1>
        <StateBlock loading={currentUserLoading} error={currentUserError} empty={!currentUserLoading && !canUseDashboard} />
        {!currentUserLoading && !canUseDashboard ? <p className="state">Admin or coadmin access required.</p> : null}
      </section>
    );
  }

  return (
    <section className="page admin-page">
      <h1>Admin Dashboard</h1>
      {notice ? <div className="panel store-notice">{notice}</div> : null}

      <div className="admin-grid">
        {isAdmin ? <Link className="panel link-card admin-action" to="/store">Manage Forum Store</Link> : null}
        {isAdmin ? <div className="panel admin-action admin-action-disabled">Manage Advertisements</div> : null}
        <a className="panel link-card admin-action" href="#review-posts">Review New Posts</a>
        <a className="panel link-card admin-action" href="#review-comments">Review Reported Comments</a>
      </div>

      {isAdmin ? (
        <section className="panel admin-section">
          <h2>Post Review Mode</h2>
          <div className="segmented">
            <button
              className={reviewMode === POST_REVIEW_MODE_OPEN ? "active" : ""}
              onClick={() => runAction(() => setPostReviewMode(POST_REVIEW_MODE_OPEN), "Review opened: user posts publish immediately.")}
            >
              Open Review
            </button>
            <button
              className={reviewMode === POST_REVIEW_MODE_CLOSED ? "active" : ""}
              onClick={() => runAction(() => setPostReviewMode(POST_REVIEW_MODE_CLOSED), "Review closed: user posts require approval.")}
            >
              Close Review
            </button>
          </div>
        </section>
      ) : null}

      {isAdmin ? (
        <section className="panel admin-section" id="manage-sections">
          <h2>Sections</h2>
          <SectionForm
            onSubmit={(input) => runAction(() => createSection(input), "Section created.")}
            submitLabel="Create Section"
          />
          <div className="list section-admin-list">
            {(sections || []).map((section) => (
              <SectionAdminRow
                key={section.id}
                section={section}
                onSave={(input) => runAction(() => updateOrSetSection(section.id, input), "Section updated.")}
                onDelete={() => runAction(() => deleteSection(section.id), "Section deleted.")}
              />
            ))}
          </div>
        </section>
      ) : null}

      <section className="panel admin-section" id="review-posts">
        <h2>Review New Posts</h2>
        <StateBlock loading={pendingLoading} error={pendingError} empty={visiblePendingPosts.length === 0 && !pendingLoading} />
        <div className="list">
          {visiblePendingPosts.map(({ post, collectionName }) => (
            <article className="admin-review-item" key={`${collectionName}:${post.id}`}>
              <div>
                <strong>{post.title}</strong>
                <p>{post.content}</p>
                <span className="meta">{collectionName}</span>
              </div>
              <div className="admin-inline-actions">
                <button className="primary-button" onClick={() => runAction(() => reviewPost(collectionName, post.id, "APPROVED", user.uid), "Post approved.")}>
                  Approve
                </button>
                <button className="danger-button" onClick={() => runAction(() => reviewPost(collectionName, post.id, "REJECTED", user.uid), "Post rejected.")}>
                  Reject
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="panel admin-section" id="review-comments">
        <h2>Review Reported Comments</h2>
        <p>Reported comment review is not wired in the web version yet.</p>
      </section>

      {isAdmin ? (
        <section className="panel admin-section">
          <h2>Users</h2>
          <div className="list">
            {(users || []).map((managedUser) => (
              <UserAdminRow
                key={managedUser.uid}
                user={managedUser}
                sections={sections || []}
                onChangeRole={(newRole) => runAction(() => updateUserRole(managedUser.uid, newRole), "Role updated.")}
                onChangeSections={(sectionIds) => runAction(() => updateManagedSections(managedUser.uid, sectionIds), "Managed sections updated.")}
              />
            ))}
          </div>
        </section>
      ) : null}
    </section>
  );
}

type SectionInput = {
  name: string;
  description: string;
  imageUrl?: string | null;
  appHtml?: string;
};

function SectionForm({
  section,
  onSubmit,
  submitLabel
}: {
  section?: Section;
  onSubmit: (input: SectionInput) => void;
  submitLabel: string;
}) {
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const input = {
      name: String(form.get("name") || "").trim(),
      description: String(form.get("description") || "").trim(),
      imageUrl: String(form.get("imageUrl") || "").trim() || null,
      appHtml: String(form.get("appHtml") || "").trim()
    };
    if (!input.name) return;
    onSubmit(input);
    if (!section) event.currentTarget.reset();
  }

  return (
    <form className="form-grid section-admin-form" onSubmit={submit}>
      <input name="name" defaultValue={section?.name || ""} placeholder="Section name" required />
      <textarea name="description" defaultValue={section?.description || ""} placeholder="Section description" />
      <input name="imageUrl" defaultValue={section?.imageUrl || ""} placeholder="Image URL" />
      <textarea
        className="code-input"
        name="appHtml"
        defaultValue={section?.appHtml || ""}
        placeholder="Optional HTML code for this section"
        spellCheck={false}
      />
      <button className="primary-button" type="submit">
        {submitLabel}
      </button>
    </form>
  );
}

function SectionAdminRow({
  section,
  onSave,
  onDelete
}: {
  section: Section;
  onSave: (input: SectionInput) => void;
  onDelete: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <article className="admin-review-item section-admin-row">
      <div>
        <strong>{section.name}</strong>
        {section.description ? <p>{section.description}</p> : null}
        <span className="meta">{section.appHtml?.trim() ? "HTML app section" : "Standard section"}</span>
      </div>
      <div className="admin-inline-actions">
        <Link className="primary-button" to={`/sections/${encodeURIComponent(section.id)}`}>
          View
        </Link>
        <button type="button" onClick={() => setExpanded((value) => !value)}>
          {expanded ? "Close" : "Edit"}
        </button>
        <button className="danger-button" type="button" onClick={onDelete}>
          Delete
        </button>
      </div>
      {expanded ? <SectionForm section={section} onSubmit={onSave} submitLabel="Save Section" /> : null}
    </article>
  );
}

function UserAdminRow({
  user,
  sections,
  onChangeRole,
  onChangeSections
}: {
  user: ForumUser;
  sections: Section[];
  onChangeRole: (role: UserRole) => void;
  onChangeSections: (sectionIds: string[]) => void;
}) {
  const managedSections = user.managedSections || [];
  const role = normalizeRole(user.role);

  function toggleSection(sectionId: string) {
    const next = managedSections.includes(sectionId)
      ? managedSections.filter((id) => id !== sectionId)
      : [...managedSections, sectionId];
    onChangeSections(next);
  }

  return (
    <article className="admin-user-row">
      <Link className="message-author" to={`/users/${encodeURIComponent(user.uid)}`}>
        <UserAvatar name={displayUserName(user)} imageUrl={user.profilePictureUrl} />
        <span>
          <strong>{displayUserName(user)}</strong>
          <span>{role}</span>
        </span>
      </Link>
      <select value={role} onChange={(event) => onChangeRole(event.target.value as UserRole)}>
        {roles.map((item) => (
          <option key={item} value={item}>{item}</option>
        ))}
      </select>
      {role === "COADMIN" ? (
        <div className="admin-section-picker">
          {sections.map((section) => (
            <label key={section.id}>
              <input type="checkbox" checked={managedSections.includes(section.id)} onChange={() => toggleSection(section.id)} />
              <span>{section.name}</span>
            </label>
          ))}
        </div>
      ) : null}
    </article>
  );
}

function normalizeRole(role?: string): UserRole {
  const normalized = role?.toUpperCase();
  if (normalized === "ADMIN" || normalized === "COADMIN") return normalized;
  return "USER";
}

function canReviewPost(user: ForumUser, item: ReviewPost) {
  const role = normalizeRole(user.role);
  if (role === "ADMIN") return true;
  if (role !== "COADMIN") return false;
  if (item.post.authorRole?.toUpperCase() === "ADMIN") return false;
  return user.managedSections?.includes(item.post.sectionId) === true;
}
